import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ApiError,
  inviteMokoTeamMember,
  listMokoTeam,
  reactivateMokoTeamMember,
  revokeMokoTeamMember,
  updateMokoTeamRole,
  type MokoRole,
  type MokoTeamInviteResponse,
  type MokoTeamMember,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { UserPlus, Ban, KeyRound, Copy, CheckCircle2, PlayCircle, ShieldCheck, LifeBuoy, Wrench, DollarSign, Briefcase } from "lucide-react";

const ROLE_LABELS: Record<MokoRole, string> = {
  admin: "Admin Moko",
  finance: "Finance Moko",
  support: "Support Moko",
  it: "IT Moko",
  commercial: "Commercial Moko",
};

const ROLE_COLORS: Record<MokoRole, "primary" | "info" | "warning" | "success" | "danger"> = {
  admin: "danger",
  finance: "info",
  support: "primary",
  it: "warning",
  commercial: "success",
};

const ROLE_ICONS: Record<MokoRole, typeof ShieldCheck> = {
  admin: ShieldCheck,
  finance: DollarSign,
  support: LifeBuoy,
  it: Wrench,
  commercial: Briefcase,
};

const ROLE_DESCRIPTIONS: Record<MokoRole, string> = {
  admin: "Accès complet cross-tenant + gestion équipe Moko + rotation de rôles.",
  finance: "Settlements, wallets, paiements en lecture — cross-tenant.",
  support: "Tickets, refunds, renvoi de callbacks — accès aux marchands.",
  it: "Observabilité, switch logs, debug technique — cross-tenant.",
  commercial: "Recherche par billet, funnel stats — cross-tenant.",
};

export function AdminMokoTeamPage() {
  const auth = useAuth();
  const me = auth.status === "authenticated" ? auth.user : null;

  const [members, setMembers] = useState<MokoTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState<MokoTeamInviteResponse | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<MokoTeamMember | null>(null);
  const [confirmReactivate, setConfirmReactivate] = useState<MokoTeamMember | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listMokoTeam();
      setMembers(r.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChangeRole = async (m: MokoTeamMember, newRole: MokoRole) => {
    if (newRole === m.role) return;
    if (!confirm(`Changer le rôle de ${m.email} en ${ROLE_LABELS[newRole]} ?`)) return;
    try {
      await updateMokoTeamRole(m.id, newRole);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Erreur");
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    try {
      await revokeMokoTeamMember(confirmRevoke.id);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Erreur");
    }
  };

  const handleReactivate = async () => {
    if (!confirmReactivate) return;
    try {
      await reactivateMokoTeamMember(confirmReactivate.id);
      setConfirmReactivate(null);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Erreur");
    }
  };

  const columns: ColumnDef<MokoTeamMember>[] = useMemo(
    () => [
      {
        key: "user",
        header: "Utilisateur",
        minWidth: 240,
        cell: (m) => (
          <div>
            <div className="text-sm font-semibold text-[#181c32]">
              {[m.first_name, m.last_name].filter(Boolean).join(" ") || m.email}
            </div>
            <div className="text-xs text-[#7e8299]">{m.email}</div>
            {m.phone_number && (
              <div className="text-xs text-[#a1a5b7] mt-0.5 font-mono">{m.phone_number}</div>
            )}
          </div>
        ),
      },
      {
        key: "role",
        header: "Rôle",
        minWidth: 200,
        cell: (m) => {
          const Icon = ROLE_ICONS[m.role];
          const canEdit = me?.id !== m.id;
          return (
            <div className="flex items-center gap-2">
              <Icon size={14} className="text-[#7e8299]" />
              {canEdit ? (
                <select
                  value={m.role}
                  onChange={(e) => onChangeRole(m, e.target.value as MokoRole)}
                  className="text-xs border border-[#eff2f5] rounded-md px-2 py-1 bg-white focus:outline-none focus:border-brand-500"
                >
                  {(Object.keys(ROLE_LABELS) as MokoRole[]).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              ) : (
                <Badge color={ROLE_COLORS[m.role]}>{ROLE_LABELS[m.role]}</Badge>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        header: "Statut",
        minWidth: 110,
        cell: (m) => (
          <Badge color={m.status === "active" ? "success" : "danger"}>
            {m.status === "active" ? "Actif" : "Suspendu"}
          </Badge>
        ),
      },
      {
        key: "must_change",
        header: "MDP",
        minWidth: 90,
        cell: (m) =>
          m.must_change_password ? (
            <span className="inline-flex items-center gap-1 text-xs text-warning-700">
              <KeyRound size={12} />À changer
            </span>
          ) : (
            <span className="text-xs text-[#a1a5b7]">—</span>
          ),
      },
      {
        key: "last_login",
        header: "Dernière connexion",
        minWidth: 160,
        cell: (m) => (
          <span className="text-xs text-[#7e8299]">{m.last_login_at ? formatRelative(m.last_login_at) : "Jamais"}</span>
        ),
      },
      {
        key: "created",
        header: "Créé le",
        minWidth: 120,
        cell: (m) => <span className="text-xs text-[#7e8299]">{formatDate(m.created_at)}</span>,
      },
      {
        key: "actions",
        header: "",
        minWidth: 140,
        cell: (m) => {
          if (me?.id === m.id) return <span className="text-xs text-[#a1a5b7]">— (vous)</span>;
          return m.status === "active" ? (
            <button
              onClick={() => setConfirmRevoke(m)}
              className="inline-flex items-center gap-1 text-xs text-danger-700 hover:bg-danger-50 px-2 py-1 rounded-md transition-colors"
            >
              <Ban size={12} /> Suspendre
            </button>
          ) : (
            <button
              onClick={() => setConfirmReactivate(m)}
              className="inline-flex items-center gap-1 text-xs text-success-700 hover:bg-success-50 px-2 py-1 rounded-md transition-colors"
            >
              <PlayCircle size={12} /> Réactiver
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me?.id],
  );

  return (
    <div>
      <PageHeader
        title="Équipe Moko"
        breadcrumbs={[{ label: "Administration" }, { label: "Équipe Moko" }]}
        actions={
          <button
            onClick={() => {
              setInviteResult(null);
              setInviteOpen(true);
            }}
            className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <UserPlus size={16} /> Inviter un membre
          </button>
        }
      />

      <RolesLegend />

      {error && (
        <div className="mb-4 rounded-xl bg-danger-50 border border-danger-100 text-danger-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <DataTable<MokoTeamMember>
        title="Membres équipe Moko"
        subtitle={`${members.length} utilisateur${members.length > 1 ? "s" : ""} cross-tenant`}
        columns={columns}
        data={members}
        rowKey={(m) => m.id}
        loading={loading}
      />

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={(res) => {
          setInviteResult(res);
          load();
        }}
      />

      {inviteResult && (
        <InviteResultModal result={inviteResult} onClose={() => setInviteResult(null)} />
      )}

      {confirmRevoke && (
        <Modal open onClose={() => setConfirmRevoke(null)} title="Suspendre ce compte">
          <div className="space-y-4">
            <p className="text-sm text-[#3f4254]">
              L'utilisateur <strong>{confirmRevoke.email}</strong> ne pourra plus se connecter. Vous pourrez le réactiver
              à tout moment.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmRevoke(null)}
                className="px-4 py-2 text-sm rounded-lg hover:bg-[#f5f6fa]"
              >
                Annuler
              </button>
              <button
                onClick={handleRevoke}
                className="px-4 py-2 text-sm rounded-lg bg-danger-600 hover:bg-danger-700 text-white font-semibold"
              >
                Suspendre
              </button>
            </div>
          </div>
        </Modal>
      )}

      {confirmReactivate && (
        <Modal open onClose={() => setConfirmReactivate(null)} title="Réactiver ce compte">
          <div className="space-y-4">
            <p className="text-sm text-[#3f4254]">
              L'utilisateur <strong>{confirmReactivate.email}</strong> pourra à nouveau se connecter avec son mot de
              passe existant.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmReactivate(null)}
                className="px-4 py-2 text-sm rounded-lg hover:bg-[#f5f6fa]"
              >
                Annuler
              </button>
              <button
                onClick={handleReactivate}
                className="px-4 py-2 text-sm rounded-lg bg-success-600 hover:bg-success-700 text-white font-semibold"
              >
                Réactiver
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RolesLegend() {
  return (
    <div className="mb-4 rounded-xl bg-white border border-[#eff2f5] p-4">
      <div className="text-xs font-semibold text-[#4a4a6a] mb-3">Rôles et permissions</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {(Object.keys(ROLE_LABELS) as MokoRole[]).map((r) => {
          const Icon = ROLE_ICONS[r];
          return (
            <div key={r} className="rounded-lg border border-[#eff2f5] p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon size={12} className="text-[#7e8299]" />
                <span className="text-xs font-semibold text-[#181c32]">{ROLE_LABELS[r]}</span>
              </div>
              <div className="text-[11px] text-[#7e8299] leading-snug">{ROLE_DESCRIPTIONS[r]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InviteModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (r: MokoTeamInviteResponse) => void;
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<MokoRole>("support");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setPhone("");
      setRole("support");
      setErr(null);
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const res = await inviteMokoTeamMember({
        email,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        phone_number: phone || undefined,
        role,
      });
      onSuccess(res);
      onClose();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Inviter un membre Moko">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#4a4a6a] mb-1">Prénom</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[#eff2f5] rounded-lg focus:outline-none focus:border-brand-500"
              placeholder="Iqmah"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#4a4a6a] mb-1">Nom</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[#eff2f5] rounded-lg focus:outline-none focus:border-brand-500"
              placeholder="DOE"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#4a4a6a] mb-1">Email <span className="text-danger-600">*</span></label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#eff2f5] rounded-lg focus:outline-none focus:border-brand-500"
            placeholder="prenom.nom@mokoafrika.com"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#4a4a6a] mb-1">Téléphone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#eff2f5] rounded-lg focus:outline-none focus:border-brand-500 font-mono"
            placeholder="243828584688"
          />
          <p className="text-[11px] text-[#a1a5b7] mt-1">Format E.164 sans +. Optionnel — utilisé pour SMS OTP futur.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#4a4a6a] mb-1">Rôle <span className="text-danger-600">*</span></label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as MokoRole)}
            className="w-full px-3 py-2 text-sm border border-[#eff2f5] rounded-lg focus:outline-none focus:border-brand-500 bg-white"
          >
            {(Object.keys(ROLE_LABELS) as MokoRole[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-[#a1a5b7] mt-1">{ROLE_DESCRIPTIONS[role]}</p>
        </div>

        {err && <div className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg hover:bg-[#f5f6fa]">
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting || !email}
            className="px-4 py-2 text-sm rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold disabled:opacity-50"
          >
            {submitting ? "Envoi…" : "Inviter"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteResultModal({ result, onClose }: { result: MokoTeamInviteResponse; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal open onClose={onClose} title="Invitation envoyée">
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-success-700 text-sm">
          <CheckCircle2 size={18} />
          <span>
            Compte créé pour <strong>{result.email}</strong>.
            {result.email_sent ? " Email envoyé." : " Email NON envoyé — transmets le mot de passe manuellement."}
          </span>
        </div>

        <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
          <div className="text-xs font-semibold text-warning-800 mb-2">Mot de passe temporaire (affiché une seule fois)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-warning-200 rounded px-3 py-2 font-mono text-sm break-all">
              {result.temp_password}
            </code>
            <button
              onClick={copy}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold bg-warning-600 hover:bg-warning-700 text-white rounded transition-colors"
            >
              {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
              {copied ? "Copié" : "Copier"}
            </button>
          </div>
          <p className="text-[11px] text-warning-800 mt-2">{result.warning}</p>
        </div>

        <div className="text-xs text-[#7e8299]">
          Lien de connexion : <span className="font-mono">{result.login_url}</span>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-[#181c32] text-white font-semibold">
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
}
